import { createApi } from '@reduxjs/toolkit/query/react';
import { filteredFilter } from '../../utils/api';
import {properties} from "../../properties.js";
import { authBaseQuery } from '../../app/axiosBaseQuery';
import { PaginationResult, ListRequest } from '../../types/global.d';
import * as Entity from '../types/models/ScopeGroup';
import {createNormalizationMiddleware} from "../../app/normy/normyRtkQuery.ts";

export const SCOPE_GROUP_API_REDUCER_KEY = 'scope-groups';
export const SCOPE_GROUP_PATH = 'scope-groups';

export const scopeGroupApi = createApi({
    reducerPath: SCOPE_GROUP_API_REDUCER_KEY,
    baseQuery: authBaseQuery(),
    keepUnusedDataFor: 10,
    refetchOnMountOrArgChange: 10,
    refetchOnFocus: true,
    tagTypes: ['ScopeGroup', 'ScopeGroups'],
    endpoints: (builder) => ({
        getScopeGroupList: builder.query<PaginationResult<Entity.ScopeGroup>, ListRequest>({
            query: (request) => {
                const queryParams: Record<string, any> = Object.assign({}, filteredFilter(request))
                return ({
                    url: `/${ SCOPE_GROUP_PATH }?${new URLSearchParams(queryParams).toString()}`,
                    method: 'GET',
                })
            },
            providesTags: ['ScopeGroups'],
        }),
        getScopeGroup: builder.query<Entity.ScopeGroup, Entity.PrimaryScopeGroup>({
            queryFn: async (id, { getState }, extraOptions, baseQuery) => {

                const cachedData = scopeGroupNormalizer.getObjectById(id.id);

                if (cachedData) {
                    return { data: cachedData };
                } else {
                    const response = await baseQuery({
                        url: `/${ SCOPE_GROUP_PATH }/${id.toPath()}`,
                        method: 'GET',
                    });

                    return response;
                }
            },
            providesTags: ['ScopeGroup'],
        }),
        getScopeGroupMultiple: builder.query<Entity.ScopeGroup, Entity.PrimaryScopeGroup[]>({
            query: (ids) => {
                return ({
                    url: `/${ SCOPE_GROUP_PATH }/ids${Entity.PrimaryScopeGroup.toQueryParams(ids)}`,
                    method: 'GET',
                })
            },
            providesTags: ['ScopeGroups'],
        }),
        getScopeGroupPresentation: builder.query<PaginationResult<Entity.ScopeGroup>, Entity.PresentationScopeGroupRequest>({
            query: (request) => {
              const queryParams: Record<string, any> = Object.assign({}, filteredFilter(request.list))
              return ({
                url: `/${ SCOPE_GROUP_PATH }/presentation?${new URLSearchParams(queryParams).toString()}`,
                method: 'POST',
                data: request.filter,
              })
            },
            providesTags: ['ScopeGroups'],
        }),
        getScopeGroupAdvancedPresentation: builder.query<PaginationResult<Entity.ScopeGroup>, Entity.FilterAdvancedPresentationRequest>({
            query: (request) => {
                const queryParams: Record<string, any> = Object.assign({}, filteredFilter(request.list))
                return ({
                    url: `/${ SCOPE_GROUP_PATH }/find/advancedPresentation?${new URLSearchParams(queryParams).toString()}`,
                    method: 'POST',
                    data: request.filter,
                })
            },
            providesTags: ['ScopeGroups'],
        }),
        
        insertScopeGroup: builder.mutation<Entity.ScopeGroup, Entity.CreateScopeGroup>({
            query: (toInsert) => {
                return ({
                    url: `/${ SCOPE_GROUP_PATH }`,
                    method: 'POST',
                    data: toInsert,
                })
            },
            onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
                const { data: mutationData } = await queryFulfilled;
                dispatch(
                    scopeGroupApi.util.upsertQueryData('getScopeGroup', Entity.PrimaryScopeGroup.fromId(mutationData.id), mutationData),
                );
            },
            invalidatesTags: ['ScopeGroups'],
        }),
        insertMultipleScopeGroup: builder.mutation<Entity.ScopeGroup[], Entity.CreateScopeGroup[]>({
            query: (toInsert) => {
                return ({
                    url: `/${ SCOPE_GROUP_PATH }/batch`,
                    method: 'POST',
                    data: toInsert,
                })
            },
            onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
                const { data: mutationData } = await queryFulfilled;
                for(const item of mutationData) {
                    dispatch(
                            scopeGroupApi.util.upsertQueryData('getScopeGroup', Entity.PrimaryScopeGroup.fromId(item.id), item),
                    );
                }
            },
            invalidatesTags: ['ScopeGroups'],
        }),
        updateScopeGroup: builder.mutation<Entity.ScopeGroup, Entity.UpdateEntityScopeGroup>({
            query: (toUpdate) => {
                return ({
                    url: `/${ SCOPE_GROUP_PATH }/${toUpdate.id.toPath()}`,
                    method: 'PUT',
                    data: toUpdate.data,
                })
            },
            invalidatesTags: ['ScopeGroups'],
        }),
        updateMultipleScopeGroup: builder.mutation<Entity.ScopeGroup[], Entity.UpdateEntityScopeGroup[]>({
            query: (toUpdate) => {
                return ({
                    url: `/${ SCOPE_GROUP_PATH }/batch`,
                    method: 'PUT',
                    data: toUpdate,
                })
            },
            invalidatesTags: ['ScopeGroups'],
        }),
        deleteScopeGroup: builder.mutation<Entity.ScopeGroup, Entity.ScopeGroup>({
            query: (toDelete) => {
                return ({
                    url: `/${ SCOPE_GROUP_PATH }/${Entity.PrimaryScopeGroup.fromEntity(toDelete)?.toPath()}`,
                    method: 'DELETE',
                })
            },
            invalidatesTags: ['ScopeGroups'],
        }),
        deleteMultipleScopeGroup: builder.mutation<Entity.ScopeGroup[], Entity.PrimaryScopeGroup[]>({
            query: (ids) => {
                return ({
                    url: `/${ SCOPE_GROUP_PATH }/batch${Entity.PrimaryScopeGroup.toQueryParams(ids)}`,
                    method: 'DELETE',
                })
            },
            invalidatesTags: ['ScopeGroups'],
        }),
    }),
})

export const [scopeGroupNormalizer, scopeGroupNormalizerMiddleware] = createNormalizationMiddleware(SCOPE_GROUP_API_REDUCER_KEY, scopeGroupApi, {
    devLogging: properties.debugLog,
})

export const {
    useGetScopeGroupListQuery,
    useGetScopeGroupMultipleQuery,
    useGetScopeGroupPresentationQuery,
    useGetScopeGroupQuery,
    useInsertScopeGroupMutation,
    useInsertMultipleScopeGroupMutation,
    useUpdateScopeGroupMutation,
    useUpdateMultipleScopeGroupMutation,
    useDeleteScopeGroupMutation,
    useDeleteMultipleScopeGroupMutation,
    useGetScopeGroupAdvancedPresentationQuery,
    
} = scopeGroupApi
